import type { PermissionRequirement } from "@/types/rbac.types";

/**
 * Alçada das solicitações (CAAS-4550), espelhando o `@Permissions` dos endpoints
 * correspondentes no caas-super-app. Front e backend têm que dizer a mesma coisa:
 * front mais permissivo mostra um botão que a API responde 403; front mais
 * restrito esconde uma ação que a pessoa tem direito de fazer — e os dois já
 * aconteceram nesta tela.
 *
 * Mora em `lib/` e não dentro de uma página porque as mesmas regras valem em
 * lugares diferentes: a tela de Solicitações e o detalhe do cliente abrem o
 * mesmo `POST /profile-blocks`. Antes desta task cada tela tinha a sua regra, e
 * elas divergiram.
 *
 * O par `{ role, permission }` é obrigatório nos dois. Sem `permission`,
 * `checkPermission` casa com qualquer permission da role, e o perfil Suporte é
 * `BACKOFFICE` / `READ_ONLY` — ou seja, entraria de carona sem erro e sem teste
 * vermelho. Ver A22 do `ARMADILHAS.md`.
 */

/**
 * Quem pode ABRIR solicitação (bloqueio/desbloqueio, ajuste, transferência) e
 * criar profile.
 *
 * Espelha: `POST /profile-blocks`, `POST /balance-adjustments` e `POST /clients`.
 */
export const OPEN_SOLICITATION_REQUIREMENTS: PermissionRequirement[] = [
  { role: "ADMINISTRACAO", permission: "FULL_ACCESS" },
  { role: "BACKOFFICE", permission: "FULL_ACCESS" },
];

/**
 * Quem pode APROVAR ou REJEITAR uma solicitação.
 *
 * Espelha: `POST /profile-blocks/:id/review` e `POST /balance-adjustments/:id/review`.
 *
 * Hoje é igual ao de abrir — e continua sendo duas constantes de propósito: são
 * duas decisões de alçada diferentes, que já foram diferentes e podem voltar a
 * ser. Colapsar as duas faria uma mudança de alçada de aprovação mexer, em
 * silêncio, em quem pode abrir.
 *
 * Isto NÃO substitui os quatro olhos: quem abre a solicitação nunca a aprova, e
 * essa regra vive no backend, independente de perfil.
 */
export const REVIEW_SOLICITATION_REQUIREMENTS: PermissionRequirement[] = [
  { role: "ADMINISTRACAO", permission: "FULL_ACCESS" },
  { role: "BACKOFFICE", permission: "FULL_ACCESS" },
];

/**
 * Quem pode operar CRÉDITO e DÉBITO diretos de saldo — a aba "Alteração de
 * saldo". Só o Administrador.
 *
 * O Gestor foi liberado para bloqueio de compra/venda e para transferência, e
 * só para isso. Crédito e débito não entraram no pedido, e não são a mesma
 * operação: transferência move saldo entre dois profiles da mesma empresa e
 * fecha em zero; crédito e débito **criam ou destroem** saldo.
 *
 * Isto esconde a aba. Quem barra de verdade é o backend, que recusa por tipo em
 * `POST /balance-adjustments` e no `:id/review` — front não é controle de
 * acesso, é conveniência para não oferecer o que a API vai recusar.
 */
export const ADJUSTMENT_TAB_REQUIREMENTS: PermissionRequirement[] = [
  { role: "ADMINISTRACAO", permission: "FULL_ACCESS" },
];

/**
 * Quem ALCANÇA a tela de Solicitações — a rota e o item de menu.
 *
 * Estava escrita à mão em dois lugares (`App.tsx` e `sidebar.tsx`), divergindo
 * deste arquivo: sem qualificar a `permission` do `ADMINISTRACAO`. É o mesmo
 * padrão de rota-e-menu escritos separado que produziu os defeitos que esta task
 * foi aberta para consertar, e o repo mostra o custo ao lado — o menu de Saque e
 * Tesouraria libera `BACKOFFICE` sem qualificar, enquanto a rota das duas exige
 * `FULL_ACCESS`, então o Suporte vê o item e toma "Acesso negado" ao clicar.
 *
 * `ADMINISTRACAO` fica sem qualificador DE PROPÓSITO, e é a única entrada deste
 * arquivo assim: `ADMINISTRACAO`/`READ_ONLY` já alcançava esta tela antes da
 * task, e é esse perfil que o `balance-adjustments-approval.spec.ts` usa para
 * provar que as ações ficam escondidas. Qualificar aqui mudaria comportamento
 * existente, que não foi pedido.
 */
export const SOLICITATION_SCREEN_REQUIREMENTS: PermissionRequirement[] = [
  { role: "ADMINISTRACAO" },
  { role: "BACKOFFICE", permission: "FULL_ACCESS" },
];
